/**
 * Input Validation and Sanitization Utilities
 * Helps prevent XSS, injection attacks, and validates user input
 */

/**
 * Email validation using RFC 5322 standard (more strict)
 * Validates proper email format with TLD requirement
 */
export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();

  // Basic length check
  if (trimmed.length === 0 || trimmed.length > 254) {
    return false;
  }

  // More strict regex that requires:
  // - Valid local part (before @)
  // - Valid domain part (after @)
  // - At least 2 character TLD (.com, .org, etc)
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(trimmed)) {
    return false;
  }

  // Additional checks
  const parts = trimmed.split('@');
  if (parts.length !== 2) {
    return false;
  }

  const [localPart, domain] = parts;

  // Local part can't be longer than 64 characters
  if (localPart.length > 64) {
    return false;
  }

  // Domain must have at least one dot and valid TLD
  const domainParts = domain.split('.');
  if (domainParts.length < 2) {
    return false;
  }

  // TLD must be at least 2 characters
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) {
    return false;
  }

  return true;
}

/**
 * Validate email for binary widget capture
 * More lenient for user experience while still secure
 */
export function isValidBinaryWidgetEmail(email: string): boolean {
  return isValidEmail(email);
}

/**
 * URL validation
 */
export function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Sanitize HTML to prevent XSS attacks
 * Removes all HTML tags and dangerous characters
 */
export function sanitizeHtml(input: string): string {
  const temp = document.createElement('div');
  temp.textContent = input;
  return temp.innerHTML;
}

/**
 * Sanitize user input for display
 * Trims whitespace and limits length
 */
export function sanitizeInput(input: string, maxLength = 1000): string {
  return input.trim().slice(0, maxLength);
}

/**
 * Validate widget name
 * Allow alphanumeric, spaces, hyphens, underscores, periods, and common punctuation
 */
export function isValidWidgetName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 100) return false;
  return /^[a-zA-Z0-9\s\-_.,'!?()]+$/.test(trimmed);
}

/**
 * Validate project name
 * Allow alphanumeric, spaces, hyphens, underscores, periods
 */
export function isValidProjectName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 100) return false;
  return /^[a-zA-Z0-9\s\-_.]+$/.test(trimmed);
}

/**
 * Sanitize text input - strips HTML tags and trims
 * Use for any user-provided text before storing/displaying
 */
export function sanitizeText(input: string, maxLength = 500): string {
  return input
    .replace(/<[^>]*>/g, '') // Strip HTML tags
    .replace(/[<>"'&]/g, char => {
      const entities: Record<string, string> = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;',
      };
      return entities[char] || char;
    })
    .trim()
    .slice(0, maxLength);
}

/**
 * Validate widget question text - strip HTML, ensure non-empty
 */
export function isValidWidgetQuestion(question: string): boolean {
  if (!question || question.length > 500) return false;
  const stripped = question.replace(/<[^>]*>/g, '');
  return stripped.trim().length > 0;
}

/**
 * Validate color hex code
 */
export function isValidHexColor(color: string): boolean {
  return /^#([0-9A-F]{3}){1,2}$/i.test(color);
}

/**
 * Sanitize and validate budget string
 */
export function isValidBudget(budget: string): boolean {
  const validBudgets = ['$0-100', '$100-500', '$500+', 'Custom'];
  return validBudgets.includes(budget);
}

/**
 * Validate feedback comment
 */
export function isValidComment(comment: string): boolean {
  const trimmed = comment.trim();
  return trimmed.length <= 2000;
}

/**
 * Generic string validator
 */
export function validateString(
  value: string,
  options: {
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    required?: boolean;
  } = {}
): { valid: boolean; error?: string } {
  const { minLength = 0, maxLength = Infinity, pattern, required = false } = options;

  const trimmed = value.trim();

  if (required && trimmed.length === 0) {
    return { valid: false, error: 'This field is required' };
  }

  if (trimmed.length < minLength) {
    return {
      valid: false,
      error: `Must be at least ${minLength} characters`,
    };
  }

  if (trimmed.length > maxLength) {
    return {
      valid: false,
      error: `Must be no more than ${maxLength} characters`,
    };
  }

  if (pattern && !pattern.test(trimmed)) {
    return { valid: false, error: 'Invalid format' };
  }

  return { valid: true };
}

/**
 * Escape special regex characters
 */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Rate limiting helper (client-side)
 */
export class RateLimiter {
  private attempts: Map<string, number[]> = new Map();

  constructor(
    private maxAttempts: number,
    private windowMs: number
  ) {}

  isAllowed(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);

    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return true;
  }

  reset(key: string): void {
    this.attempts.delete(key);
  }
}
